import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Ticket as TicketIcon, Package, AlertCircle, CheckCircle2, Clock, Users2, AlertTriangle, Brain, ShieldAlert, MessageCircle } from "lucide-react";

const priorityColor = {
  critica: "bg-red-100 text-red-800 border-red-200",
  alta: "bg-amber-100 text-amber-800 border-amber-200",
  media: "bg-zinc-100 text-zinc-800 border-zinc-200",
  bassa: "bg-zinc-50 text-zinc-600 border-zinc-200",
};
const statusColor = {
  aperto: "bg-red-50 text-red-700 border-red-200",
  in_lavorazione: "bg-amber-50 text-amber-800 border-amber-200",
  risolto: "bg-emerald-50 text-emerald-800 border-emerald-200",
  chiuso: "bg-zinc-100 text-zinc-700 border-zinc-200",
};

function Kpi({ label, value, icon: Icon, tone = "default", testid }) {
  return (
    <Card data-testid={testid} className="rounded-sm border-zinc-200 p-5 bg-white">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
          <div className="mt-3 font-display text-4xl font-bold tracking-tighter text-zinc-950">
            {value}
          </div>
        </div>
        <div
          className={`h-9 w-9 rounded-sm flex items-center justify-center ${
            tone === "brand" ? "bg-brand-muted text-brand" : "bg-zinc-100 text-zinc-700"
          }`}
        >
          <Icon className="h-4 w-4" strokeWidth={1.75} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [kb, setKb] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/knowledge/stats").then((r) => setKb(r.data)).catch(() => {});
  }, []);
  /* toast/formatApiError/api are module-level imports and are stable references. */

  if (!stats) return <div className="p-8 text-sm text-zinc-500">{t("common.loading")}</div>;

  return (
    <div className="p-4 sm:p-8 max-w-7xl">
      <PageHeader
        title={t("dashboard.title")}
        description={t("dashboard.description")}
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 staggered">
        <Kpi label={t("dashboard.kpi_open")} value={stats.totals.open} icon={AlertCircle} tone="brand" testid="kpi-open" />
        <Kpi label={t("dashboard.kpi_in_progress")} value={stats.totals.in_progress} icon={Clock} testid="kpi-progress" />
        <Kpi label={t("dashboard.kpi_resolved")} value={stats.totals.resolved} icon={CheckCircle2} testid="kpi-resolved" />
        <Kpi label={t("dashboard.kpi_dispensers_active")} value={stats.totals.dispensers_active} icon={Package} testid="kpi-dispensers" />
      </div>

      {stats.totals.orphan_tickets > 0 && (
        <Link
          to="/tickets?orphan=1"
          data-testid="kpi-orphan-link"
          className="mt-4 flex items-center justify-between rounded-sm border border-orange-200 bg-orange-50 px-5 py-3 hover:bg-orange-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-orange-100 text-orange-700 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-orange-800">{t("dashboard.orphan_eyebrow")}</div>
              <div className="text-sm text-orange-900 mt-0.5">
                <span className="font-display font-bold text-lg mr-1">{stats.totals.orphan_tickets}</span>
                {t("dashboard.orphan_text")}
              </div>
            </div>
          </div>
          <span className="text-xs uppercase tracking-widest text-orange-800 hover:underline">{t("dashboard.orphan_cta")}</span>
        </Link>
      )}

      {stats.totals.whatsapp_open_tickets > 0 && (
        <Link
          to="/tickets?source=whatsapp"
          data-testid="kpi-whatsapp-link"
          className="mt-4 flex items-center justify-between rounded-sm border border-emerald-200 bg-emerald-50 px-5 py-3 hover:bg-emerald-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <MessageCircle className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-900">{t("dashboard.whatsapp_eyebrow")}</div>
              <div className="text-sm text-emerald-950 mt-0.5">
                <span className="font-display font-bold text-lg mr-1">{stats.totals.whatsapp_open_tickets}</span>
                {t("dashboard.whatsapp_text")}
              </div>
            </div>
          </div>
          <span className="text-xs uppercase tracking-widest text-emerald-900 hover:underline">{t("dashboard.whatsapp_cta")}</span>
        </Link>
      )}

      {stats.totals.expiring_warranty_60d > 0 && (
        <Link
          to="/erogatori?expiring=60"
          data-testid="kpi-expiring-warranty-link"
          className="mt-4 flex items-center justify-between rounded-sm border border-amber-200 bg-amber-50 px-5 py-3 hover:bg-amber-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-amber-100 text-amber-800 flex items-center justify-center">
              <ShieldAlert className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-900">{t("dashboard.warranty_eyebrow")}</div>
              <div className="text-sm text-amber-950 mt-0.5">
                <span className="font-display font-bold text-lg mr-1">{stats.totals.expiring_warranty_60d}</span>
                {t("dashboard.warranty_text")}
              </div>
            </div>
          </div>
          <span className="text-xs uppercase tracking-widest text-amber-900 hover:underline">{t("dashboard.warranty_cta")}</span>
        </Link>
      )}

      {kb && (
        <Link
          to="/conoscenza"
          data-testid="kpi-kb-link"
          className="mt-4 flex items-center justify-between rounded-sm border border-zinc-200 bg-white px-5 py-3 hover:bg-zinc-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className={`h-8 w-8 rounded-sm flex items-center justify-center ${kb.is_ready ? "bg-emerald-100 text-emerald-700" : "bg-brand-muted text-brand"}`}>
              <Brain className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{t("dashboard.kb_title")}</div>
              <div className="text-sm text-zinc-900 mt-0.5 flex items-center gap-2">
                <span className="font-display font-bold text-lg">{kb.total}</span>
                <span>/ {kb.threshold} {t("dashboard.kb_entries_suffix")}</span>
                <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${kb.is_ready ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                  {kb.is_ready ? t("dashboard.kb_ready") : t("dashboard.kb_learning")}
                </span>
              </div>
              <div className="mt-2 h-1.5 w-56 bg-zinc-100 rounded-sm overflow-hidden">
                <div className={`h-full ${kb.is_ready ? "bg-emerald-500" : "bg-brand"}`} style={{ width: `${Math.min(100, kb.readiness_pct)}%` }} />
              </div>
            </div>
          </div>
          <span className="text-xs uppercase tracking-widest text-brand hover:underline">{t("dashboard.kb_manage")}</span>
        </Link>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <Card className="rounded-sm border-zinc-200 p-6 bg-white lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-semibold tracking-tight text-zinc-900">
              {t("dashboard.recent_tickets")}
            </h2>
            <Link
              to="/tickets"
              data-testid="dash-view-all-tickets"
              className="text-xs uppercase tracking-widest text-brand hover:underline"
            >
              {t("dashboard.view_all")}
            </Link>
          </div>
          <div className="divide-y divide-zinc-200">
            {stats.recent_tickets.length === 0 && (
              <div className="text-sm text-zinc-500 py-6">{t("dashboard.no_recent_tickets")}</div>
            )}
            {stats.recent_tickets.map((tk) => (
              <Link
                to={`/tickets/${tk.id}`}
                key={tk.id}
                data-testid={`dash-recent-${tk.id}`}
                className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-2 px-2 transition-colors"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {tk.ticket_number}
                    </span>
                    <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${priorityColor[tk.priority]}`}>
                      {t(`tickets.priority.${tk.priority}`, tk.priority)}
                    </Badge>
                  </div>
                  <div className="text-sm font-medium text-zinc-900 mt-1 truncate">{tk.title}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    S/N {tk.dispenser_serial} · {tk.dispenser?.model || "—"}
                  </div>
                </div>
                <Badge className={`rounded-sm text-[10px] uppercase tracking-widest border ${statusColor[tk.status]}`}>
                  {t(`tickets.status.${tk.status}`, tk.status.replace("_", " "))}
                </Badge>
              </Link>
            ))}
          </div>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-sm border-zinc-200 p-6 bg-white">
            <h2 className="font-display text-lg font-semibold tracking-tight text-zinc-900 mb-4">
              {t("dashboard.by_priority_active")}
            </h2>
            <div className="space-y-3">
              {Object.entries(stats.priorities).map(([p, v]) => (
                <div key={p} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-700">{t(`tickets.priority.${p}`, p)}</span>
                  <span className="font-display font-bold text-zinc-950">{v}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-sm border-zinc-200 p-6 bg-white">
            <div className="flex items-center gap-2 mb-4">
              <Users2 className="h-4 w-4 text-zinc-500" />
              <h2 className="font-display text-lg font-semibold tracking-tight text-zinc-900">
                {t("dashboard.technicians_load")}
              </h2>
            </div>
            <div className="space-y-2">
              {stats.per_technician.length === 0 && (
                <div className="text-sm text-zinc-500">{t("dashboard.no_technicians")}</div>
              )}
              {stats.per_technician.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between text-sm">
                  <span className="text-zinc-700">{u.name}</span>
                  <span className="font-display font-bold text-zinc-950">{u.open_count}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
